/**
 * Historical EOD Calendar — Reports tab (2026-02).
 *
 * READ-ONLY visual index of past EOD reports. NOT a dashboard, NOT
 * an analytics engine. Purpose: make it easy to find and open the
 * existing EOD report for any past date.
 *
 * Data flow:
 *   • On mount / month change / branch change → GET
 *     `/api/reports/eod/calendar?year=&month=&branch_id=` which
 *     returns one row per calendar day using the same source
 *     collections + canonicalisation as the full EOD compute.
 *   • Hovering (desktop) or tapping (mobile) a day fills an inline
 *     preview panel below the grid with a compact Clinical + Financial
 *     + Cash Handover summary.
 *   • Clicking a day (desktop) — or tapping "Open Full EOD" in the
 *     preview (mobile) — navigates to `/reports/eod?date=YYYY-MM-DD&branch=<id>`,
 *     the EXISTING EOD page. No second EOD renderer.
 *
 * Cash Handover follows the CURRENT Cash Drawer model verbatim:
 *   Closing = Opening + Today's Cash Collection
 * No variance, no outflow subtraction, no actual-cash count — those
 * are out of scope of this task.
 *
 * Branch scope: the backend auto-scopes single-branch users; the
 * dropdown here only appears when the caller can see > 1 branch.
 * Direct URL navigation into the underlying EOD page still enforces
 * `require_roles(*EOD_ROLES)` server-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmtINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const todayYmd = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

export default function EodCalendarView() {
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [previewYmd, setPreviewYmd] = useState(null);

  // Distinguish real hover devices from touch — hover-only preview
  // triggers should not fire on tablets that mimic pointer events.
  const isTouch = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches,
    [],
  );

  useEffect(() => {
    axios.get(`${API}/branches`)
      .then((r) => setBranches(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBranches([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr(''); setPreviewYmd(null);
    try {
      const params = { year, month };
      if (branchId) params.branch_id = branchId;
      const r = await axios.get(`${API}/reports/eod/calendar`, { params });
      setDays(Array.isArray(r.data?.days) ? r.data.days : []);
    } catch (e) {
      setDays([]);
      setErr(e?.response?.data?.detail || 'Unable to load calendar.');
    } finally {
      setLoading(false);
    }
  }, [year, month, branchId]);

  useEffect(() => { load(); }, [load]);

  const goPrev = () => {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else setMonth((m) => m - 1);
  };
  const goNext = () => {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else setMonth((m) => m + 1);
  };

  const monthLabel = useMemo(() => {
    const d = new Date(year, month - 1, 1);
    return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  }, [year, month]);

  const dayByDate = useMemo(() => {
    const map = new Map();
    for (const d of days) map.set(d.date, d);
    return map;
  }, [days]);

  const daysInMonth = useMemo(() => new Date(year, month, 0).getDate(), [year, month]);
  const startWeekday = useMemo(() => new Date(year, month - 1, 1).getDay(), [year, month]);

  const cells = useMemo(() => {
    const out = [];
    for (let i = 0; i < startWeekday; i++) out.push({ blank: true, key: `b${i}` });
    for (let d = 1; d <= daysInMonth; d++) {
      const ymd = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      out.push({ blank: false, key: ymd, day: d, ymd, data: dayByDate.get(ymd) });
    }
    return out;
  }, [startWeekday, daysInMonth, year, month, dayByDate]);

  const tYmd = useMemo(() => todayYmd(), []);
  const preview = previewYmd ? dayByDate.get(previewYmd) : null;

  const openEod = useCallback((ymd) => {
    const qs = new URLSearchParams();
    qs.set('date', ymd);
    if (branchId) qs.set('branch', branchId);
    navigate(`/reports/eod?${qs.toString()}`);
  }, [branchId, navigate]);

  const onCellClick = (cell) => {
    if (cell.blank) return;
    if (cell.ymd > tYmd) {
      // Future date — no fabricated EOD; still allow tap-preview so
      // touch users see the "no activity" state instead of navigating.
      setPreviewYmd(cell.ymd);
      return;
    }
    if (isTouch) {
      // Mobile: tap fills preview panel; navigation happens via the
      // "Open Full EOD" button inside the panel.
      setPreviewYmd(cell.ymd);
    } else {
      // Desktop: hover has already shown the panel, click goes to full
      // EOD directly. Matches the spec's Desktop behaviour.
      openEod(cell.ymd);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 sm:p-4" data-testid="eod-calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays size={14} className="text-emerald-700" />
          <span className="text-sm font-bold text-slate-900">Historical EOD Calendar</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button" onClick={goPrev} data-testid="eod-cal-prev"
            aria-label="Previous month"
            className="p-1.5 border border-slate-300 rounded hover:bg-slate-50"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-xs sm:text-sm font-bold text-slate-900 min-w-[130px] text-center" data-testid="eod-cal-month">
            {monthLabel}
          </span>
          <button
            type="button" onClick={goNext} data-testid="eod-cal-next"
            aria-label="Next month"
            className="p-1.5 border border-slate-300 rounded hover:bg-slate-50"
          >
            <ChevronRight size={14} />
          </button>
          {branches.length > 1 && (
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              data-testid="eod-cal-branch"
              className="text-xs border border-slate-300 rounded px-2 py-1 ml-1"
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {err && (
        <div className="mb-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="eod-cal-error">
          {err}
        </div>
      )}

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-[10px] text-slate-500 text-center font-semibold uppercase tracking-wider">
            {w}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1" data-testid="eod-cal-grid">
        {cells.map((cell) => {
          if (cell.blank) return <div key={cell.key} className="aspect-square" />;
          const isToday = cell.ymd === tYmd;
          const isFuture = cell.ymd > tYmd;
          const hasActivity = cell.data?.has_activity;
          const isPreview = previewYmd === cell.ymd;
          const base = 'aspect-square flex flex-col items-center justify-center rounded border text-xs relative select-none transition-colors';
          const stateCls = isFuture
            ? 'bg-slate-50 text-slate-300 border-slate-200'
            : hasActivity
              ? 'bg-emerald-50 text-emerald-900 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-500 cursor-pointer'
              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 cursor-pointer';
          const highlight = isToday ? 'ring-2 ring-emerald-400 font-bold' : '';
          const previewCls = isPreview ? 'ring-2 ring-sky-400' : '';
          return (
            <button
              key={cell.key}
              type="button"
              onClick={() => onCellClick(cell)}
              onMouseEnter={() => !isTouch && setPreviewYmd(cell.ymd)}
              onMouseLeave={() => !isTouch && setPreviewYmd(null)}
              data-testid={`eod-cal-day-${cell.ymd}`}
              data-has-activity={hasActivity ? 'true' : 'false'}
              className={`${base} ${stateCls} ${highlight} ${previewCls}`}
            >
              <span>{cell.day}</span>
              {hasActivity && !isFuture && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 absolute bottom-1"></span>
              )}
            </button>
          );
        })}
      </div>

      {loading && <div className="text-center text-[11px] text-slate-400 italic py-2">Loading…</div>}

      {/* Preview panel — inline below the grid. Fills on hover
          (desktop) or tap (mobile). Same panel drives both modes so
          keyboard / screen-reader / print users get a single source. */}
      <div className="mt-3 border-t border-slate-200 pt-3 min-h-[64px]" data-testid="eod-cal-preview">
        {preview ? (
          <PreviewPanel day={preview} onOpenEod={() => openEod(preview.date)} />
        ) : (
          <div className="text-[11px] text-slate-400 italic text-center py-3">
            {isTouch
              ? 'Tap a date to preview its EOD summary. Tap "Open Full EOD" to see the full report.'
              : 'Hover a date to preview its EOD summary. Click a highlighted date to open the full report.'}
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewPanel({ day, onOpenEod }) {
  const hasActivity = !!day?.has_activity;
  const ps = day.patient_summary || {};
  const tp = day.tests_performed || { by_type: {} };
  const co = day.collections || { by_method: {} };
  const ch = day.cash_handover || {};
  const dateLabel = useMemo(
    () => new Date(day.date + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    }),
    [day.date],
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs" data-testid="eod-cal-preview-content">
      <div className="md:col-span-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-bold text-slate-900" data-testid="eod-cal-preview-date">{dateLabel}</div>
        {hasActivity ? (
          <button
            onClick={onOpenEod}
            data-testid="eod-cal-open-full"
            className="text-[11px] font-bold px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded"
          >
            Open Full EOD Report →
          </button>
        ) : (
          <span
            className="text-[11px] italic text-slate-400"
            data-testid="eod-cal-no-activity"
          >
            No EOD data for this date.
          </span>
        )}
      </div>

      {hasActivity && (
        <>
          <Section title="Clinical" testid="eod-cal-preview-clinical">
            <Row k="New patients"  v={ps.new_patients ?? 0} />
            <Row k="Returning"     v={ps.returning_patients ?? 0} />
            <Row k="Footfall"      v={ps.total_footfall ?? 0} bold />
            <Row k="Tests"         v={tp.total ?? 0} />
            <div className="pl-2 mt-0.5 space-y-0 text-[10px] text-slate-500">
              {Object.entries(tp.by_type || {})
                .filter(([, v]) => v > 0)
                .map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span>{k}</span><span className="font-mono">{v}</span>
                  </div>
                ))}
            </div>
          </Section>
          <Section title="Financial" testid="eod-cal-preview-financial">
            <Row k="Total"  v={fmtINR(co.total_collected)} bold />
            <Row k="Cash"   v={fmtINR(co.by_method?.cash)} />
            <Row k="UPI"    v={fmtINR(co.by_method?.upi)} />
            <Row k="Card"   v={fmtINR(co.by_method?.card)} />
            <Row k="Other"  v={fmtINR(co.by_method?.other)} />
          </Section>
          <Section title="Cash Handover" testid="eod-cal-preview-cash">
            {ch.opening_cash == null ? (
              <div className="text-[10px] italic text-slate-400 py-1">
                No opening cash on file for this date / branch.
              </div>
            ) : (
              <>
                <Row k="Opening"       v={fmtINR(ch.opening_cash)} />
                <Row k="Cash today"    v={fmtINR(ch.cash_collected)} />
                <Row k="Less: Petty"   v={`− ${fmtINR(ch.petty_cash)}`} />
                <Row k="Expected"      v={fmtINR(ch.closing_cash)} bold />
                {/* Actual · Variance — Phase 1 (2026-02). Rendered only
                    when an actual count exists so historical dates
                    without a count stay visually clean. */}
                {ch.actual_cash != null && (
                  <>
                    <Row k="Actual"     v={fmtINR(ch.actual_cash)} />
                    <Row
                      k="Variance"
                      v={
                        ch.variance == null
                          ? '—'
                          : `${ch.variance > 0 ? '+ ' : ch.variance < 0 ? '− ' : ''}${fmtINR(Math.abs(ch.variance))}`
                      }
                      bold
                    />
                    {ch.variance_status && (
                      <Row k="Status" v={ch.variance_status} />
                    )}
                  </>
                )}
                {/* Bank Deposit · Cash Remaining — Phase 2A (2026-02).
                    Only rendered when at least one deposit exists on
                    the day so historical rows without deposits stay
                    visually clean. `cash_remaining` = actual −
                    bank_deposit; Expected Closing is NEVER substituted
                    for Actual (per §6 of the Phase 2A directive). */}
                {ch.bank_deposit > 0 && (
                  <>
                    <Row k="Bank Deposit" v={`− ${fmtINR(ch.bank_deposit)}`} />
                    <Row
                      k="Remaining"
                      v={ch.cash_remaining == null ? 'Not calculated' : fmtINR(ch.cash_remaining)}
                      bold
                    />
                  </>
                )}
              </>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mb-1">{title}</div>
      <div className="border border-slate-100 rounded p-1.5 bg-slate-50 space-y-0.5">
        {children}
      </div>
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className={`flex items-center justify-between text-[11px] ${bold ? 'font-bold text-slate-900' : 'text-slate-700'}`}>
      <span>{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}
